
import React from 'react';
import Header from './components/Header';
import AIChat from './components/AIChat';
import BookMockup from './components/BookMockup';
import ValueStackMockup from './components/ValueStackMockup';
import InvestmentCalculator from './components/InvestmentCalculator';
import Logo from './components/Logo';
import { MAIN_OFFER, STACK_ITEMS, TESTIMONIALS, QUALIFIERS, AUTHORITY_CONTENT } from './constants';
import { ArrowRight, Lock, XCircle, CheckCircle, ShieldCheck, Zap, Download, ChevronDown, UserCheck, Star, ShieldAlert, Target } from 'lucide-react';

const App: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-[#0f0a24]">
      {/* Scarcity Bar */}
      <div className="scarcity-bar text-white text-center py-2 text-[10px] md:text-sm font-bold tracking-wide sticky top-0 z-50 shadow-lg px-4 uppercase">
        🔥 ALERTA: Esta oferta de $6.99 expirará cuando se agoten los cupos de lanzamiento
      </div>

      <Header />

      {/* Hero Section */}
      <header className="relative pt-32 md:pt-48 pb-12 md:pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[800px] h-[500px] bg-purple-600/10 rounded-full blur-[120px] -z-10"></div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center">
          
          {/* Headline - Order 1 en Mobile */}
          <div className="order-1 space-y-4 md:space-y-6 animate-fade-in-up">
            <p className="text-[#8b5cf6] font-bold uppercase tracking-widest text-[10px] md:text-sm flex items-center gap-2">
              <Star className="w-4 h-4 fill-[#8b5cf6]" /> Acceso Instantáneo al "Underground Playbook"
            </p>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-[0.9] font-oswald uppercase text-white">
              CÓMO ELEGIR ACCIONES <br className="hidden md:block" /> 
              GANADORAS <span className="text-[#8b5cf6]">EN 15 MINUTOS</span>
            </h1>

            {/* Este bloque de texto se oculta en mobile para reordenarlo después de la imagen */}
            <div className="hidden md:block space-y-6">
              <h2 className="text-lg md:text-2xl text-gray-300 font-medium leading-snug">
                El sistema exacto para encontrar empresas extraordinarias sin depender de "expertos" de TV o corazonadas.
              </h2>
              
              <div className="bg-white/5 border-l-4 border-[#8b5cf6] p-4 md:p-6 rounded-r-lg">
                <p className="text-gray-400 italic text-sm md:text-base">
                  "El 90% de los inversores pierden dinero porque confunden esperanza con análisis. Este método elimina la esperanza y te da datos puros."
                </p>
              </div>

              <div className="flex flex-col gap-3 pt-2">
                <a href={MAIN_OFFER.hotmartLink} className="cta-button text-[#451a03] font-black text-base md:text-xl py-4 md:py-5 px-6 md:px-10 rounded-lg text-center flex items-center justify-center gap-3 group">
                  <span>{MAIN_OFFER.cta}</span>
                  <ArrowRight className="group-hover:translate-x-1 transition-transform w-5 h-5 md:w-6 md:h-6" />
                </a>
                <p className="text-center text-[10px] md:text-xs text-gray-500 flex justify-center items-center gap-2">
                  <Lock className="w-3 h-3" /> Pago 100% Seguro vía Hotmart | Acceso Inmediato
                </p>
              </div>
            </div>
          </div>

          {/* Imagen - Order 2 en Mobile, Columna Derecha en Desktop */}
          <div className="order-2 md:order-last animate-fade-in-up flex justify-center">
            <BookMockup />
          </div>

          {/* Copy para Mobile - Order 3 (Solo visible en pantallas pequeñas) */}
          <div className="order-3 md:hidden space-y-6 animate-fade-in-up">
            <h2 className="text-lg text-gray-300 font-medium leading-snug">
              El sistema exacto para encontrar empresas extraordinarias sin depender de "expertos" de TV o corazonadas.
            </h2>
            
            <div className="bg-white/5 border-l-4 border-[#8b5cf6] p-4 rounded-r-lg">
              <p className="text-gray-400 italic text-sm">
                "El 90% de los inversores pierden dinero porque confunden esperanza con análisis. Este método elimina la esperanza y te da datos puros."
              </p>
            </div>

            <div className="flex flex-col gap-3 pt-2">
              <a href={MAIN_OFFER.hotmartLink} className="cta-button text-[#451a03] font-black text-base py-4 px-6 rounded-lg text-center flex items-center justify-center gap-3 group">
                <span>{MAIN_OFFER.cta}</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform w-5 h-5" />
              </a>
              <p className="text-center text-[10px] text-gray-500 flex justify-center items-center gap-2">
                <Lock className="w-3 h-3" /> Pago 100% Seguro vía Hotmart | Acceso Inmediato
              </p>
            </div>
          </div>

        </div>
      </header>

      {/* Social Proof - Testimonials */}
      <section className="py-12 bg-black/20 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="glass-card p-6 rounded-2xl border border-white/10">
                <div className="flex gap-1 mb-4 text-gold">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-gray-300 text-sm italic mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border border-purple-500/30 overflow-hidden shrink-0">
                    <img src={t.avatar} alt={t.author} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-white">{t.author}</p>
                    <p className="text-[10px] text-purple-400 uppercase font-bold">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Epiphany Bridge - Comparación */}
      <section className="py-20 bg-[#150d33]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12 font-oswald uppercase">¿Por qué la mayoría falla en Bolsa?</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="glass-card p-8 rounded-xl border-l-4 border-red-500">
              <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center gap-2 font-oswald uppercase">
                <XCircle className="w-5 h-5" /> La Vieja Manera (Perder)
              </h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex gap-2"><span>❌</span> Comprar porque "un amigo me dijo".</li>
                <li className="flex gap-2"><span>❌</span> Entrar en pánico cuando baja el precio.</li>
                <li className="flex gap-2"><span>❌</span> Leer noticias (que siempre llegan tarde).</li>
                <li className="flex gap-2"><span>❌</span> Intentar adivinar el fondo del mercado.</li>
                <li className="flex gap-2"><span>❌</span> <strong>Resultado:</strong> Portafolio en rojo y estrés.</li>
              </ul>
            </div>

            <div className="glass-card p-8 rounded-xl border-l-4 border-[#8b5cf6] bg-purple-900/10">
              <h3 className="text-xl font-bold text-[#a78bfa] mb-4 flex items-center gap-2 font-oswald uppercase">
                <CheckCircle className="w-5 h-5" /> La Nueva Manera (Ganar)
              </h3>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex gap-2"><span>✅</span> Usar una Checklist de 6 puntos técnicos.</li>
                <li className="flex gap-2"><span>✅</span> Comprar solo cuando el precio &lt; valor.</li>
                <li className="flex gap-2"><span>✅</span> Ignorar el ruido y centrarse en el negocio.</li>
                <li className="flex gap-2"><span>✅</span> Entender el "Moat" (Foso) de la empresa.</li>
                <li className="flex gap-2"><span>✅</span> <strong>Resultado:</strong> Dormir tranquilo y ganar.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifier Section - "Para quién es" */}
      <section className="py-20 px-6 bg-black/30">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-8 font-oswald uppercase text-white border-b border-purple-500 pb-2 inline-block">¿Es esto para ti?</h2>
            <div className="space-y-6">
              {QUALIFIERS.isFor.map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="bg-emerald-500/20 p-1 rounded-full h-fit mt-1">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  </div>
                  <p className="text-gray-300 font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="opacity-60">
            <h2 className="text-2xl font-bold mb-8 font-oswald uppercase text-gray-500">No pierdas tiempo si...</h2>
            <div className="space-y-6">
              {QUALIFIERS.isNotFor.map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="bg-red-500/20 p-1 rounded-full h-fit mt-1">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <p className="text-gray-500">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Stack - El Paquete */}
      <section className="py-20 px-6 relative" id="oferta">
        <div className="max-w-4xl mx-auto border border-purple-500/30 rounded-3xl p-8 md:p-12 bg-[#1a103c] relative shadow-[0_0_80px_rgba(139,92,246,0.15)]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
             <div>
                <div className="text-left mb-10">
                  <h2 className="text-3xl md:text-5xl font-bold text-white uppercase font-oswald leading-none">Tu Nuevo Arsenal de Inversor</h2>
                  <p className="text-[#a78bfa] mt-4 font-medium italic">Acceso instantáneo a todo el material digital</p>
                </div>

                <div className="space-y-6 mb-8">
                  {STACK_ITEMS.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center border-b border-white/5 pb-4 border-dashed">
                      <div className="flex items-center gap-4">
                        <div className="shrink-0">{item.icon}</div>
                        <div>
                          <h4 className="font-bold text-base md:text-lg font-oswald uppercase text-white leading-tight">{item.title}</h4>
                          <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">{item.desc}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="font-bold text-sm md:text-base font-oswald text-[#a78bfa]">{item.value}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-left space-y-6">
                  <div className="flex flex-col">
                    <span className="text-xl text-gray-500 font-bold strike-through mb-1 w-fit">Valor Total: {MAIN_OFFER.originalValue}</span>
                    <div className="text-6xl md:text-8xl font-black text-gold tracking-tighter drop-shadow-lg font-oswald leading-none">
                      {MAIN_OFFER.price}
                    </div>
                  </div>

                  <a href={MAIN_OFFER.hotmartLink} className="cta-button w-full text-[#451a03] font-black text-xl py-6 px-8 rounded-xl flex items-center justify-center gap-3 group">
                    <span>QUIERO TODO POR {MAIN_OFFER.price}</span>
                    <Download className="w-6 h-6" />
                  </a>
                  
                  <div className="flex flex-wrap gap-4 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                    <span className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Garantía Blindada</span>
                    <span className="flex items-center gap-1"><Zap className="w-4 h-4 text-gold" /> Pago Único</span>
                  </div>
                </div>
             </div>
             
             {/* Visualización del Stack Completo */}
             <div className="hidden lg:block">
                <ValueStackMockup />
             </div>
          </div>
        </div>
      </section>

      {/* Authority Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-12 items-center bg-white/5 p-12 rounded-[3rem]">
          <div className="w-48 h-48 md:w-64 md:h-64 shrink-0 overflow-hidden rounded-[2rem] grayscale hover:grayscale-0 transition-all border-4 border-[#8b5cf6]">
            <img src={AUTHORITY_CONTENT.image} alt={AUTHORITY_CONTENT.name} className="w-full h-full object-cover" />
          </div>
          <div className="space-y-4 text-center md:text-left">
            <h3 className="text-3xl font-bold font-oswald uppercase text-[#a78bfa]">{AUTHORITY_CONTENT.title}</h3>
            <p className="text-gray-300 leading-relaxed italic">
              "{AUTHORITY_CONTENT.bio}"
            </p>
            <div className="flex justify-center md:justify-start gap-4 pt-4">
              {AUTHORITY_CONTENT.stats.map((stat, i) => (
                <div key={i} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/50">
                  {stat.icon} {stat.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 px-6 bg-black/20">
        <InvestmentCalculator />
      </section>

      {/* Risk-Free Guarantee */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center space-y-8 glass-card p-12 rounded-[3rem] border-2 border-dashed border-[#8b5cf6]/30">
          <div className="inline-block p-4 bg-[#8b5cf6]/10 rounded-full mb-4">
            <ShieldAlert className="w-16 h-16 text-[#8b5cf6] animate-bounce" />
          </div>
          <h2 className="text-4xl md:text-6xl font-black font-oswald uppercase text-white tracking-tight">Tu riesgo es Cero</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Compra el método hoy. Lee las 15 páginas. Aplica la Checklist. Si sientes que no vale al menos 10 veces lo que pagaste, envíanos un email en los primeros 7 días y te devolvemos cada centavo. <br /><span className="text-gold font-bold">Sin preguntas. Sin rencores.</span>
          </p>
          <div className="pt-6">
             <a href={MAIN_OFFER.hotmartLink} className="text-gold border-b-2 border-gold font-bold text-lg hover:text-white hover:border-white transition-all uppercase tracking-widest">
               Prueba el sistema libre de riesgo ahora
             </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#0f0a24]">
        <div className="max-w-2xl mx-auto px-6 space-y-8">
          <h2 className="text-3xl font-bold text-center font-oswald uppercase text-white">Preguntas Frecuentes</h2>
          
          <div className="space-y-4">
            <details className="group bg-white/5 rounded-xl p-6 cursor-pointer border border-white/5 transition-all">
              <summary className="font-bold flex justify-between items-center list-none select-none font-oswald text-lg uppercase text-white">
                ¿Necesito saber matemáticas?
                <ChevronDown className="transition group-open:rotate-180 w-5 h-5 text-[#8b5cf6]" />
              </summary>
              <p className="text-gray-400 mt-4 text-sm leading-relaxed">No. Si sabes sumar y restar, puedes usar este método. Las herramientas online hacen los cálculos por ti, nosotros te enseñamos qué buscar y cómo interpretar los resultados.</p>
            </details>

            <details className="group bg-white/5 rounded-xl p-6 cursor-pointer border border-white/5 transition-all">
              <summary className="font-bold flex justify-between items-center list-none select-none font-oswald text-lg uppercase text-white">
                ¿Funciona para cualquier mercado?
                <ChevronDown className="transition group-open:rotate-180 w-5 h-5 text-[#8b5cf6]" />
              </summary>
              <p className="text-gray-400 mt-4 text-sm leading-relaxed">Sí. Los principios de valoración y calidad funcionan en Wall Street, Europa o Mercados Emergentes. Un buen negocio es un buen negocio en todas partes del mundo.</p>
            </details>

            <details className="group bg-white/5 rounded-xl p-6 cursor-pointer border border-white/5 transition-all">
              <summary className="font-bold flex justify-between items-center list-none select-none font-oswald text-lg uppercase text-white">
                ¿Por qué es tan barato ($6.99)?
                <ChevronDown className="transition group-open:rotate-180 w-5 h-5 text-[#8b5cf6]" />
              </summary>
              <p className="text-gray-400 mt-4 text-sm leading-relaxed">Es nuestra "Oferta Irresistible". Queremos que nos conozcas sin barreras de precio. Una vez que veas la calidad de nuestro material, querrás nuestros programas avanzados. Es una situación Ganar-Ganar.</p>
            </details>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-16 text-center bg-black/40">
        <div className="max-w-2xl mx-auto px-6">
          <div className="mb-8 scale-75 md:scale-90">
            <Logo />
          </div>
          <p className="text-gray-600 text-xs mb-4">© 2025 Invesmart. Todos los derechos reservados.</p>
          <p className="text-gray-700 text-[10px] leading-relaxed max-w-lg mx-auto">
            Descargo de responsabilidad: Los resultados pueden variar. Invertir conlleva riesgos significativos. Este producto es estrictamente educativo y no constituye asesoría financiera legal ni personalizada.
          </p>
        </div>
      </footer>

      <AIChat />
    </div>
  );
};

export default App;
